import * as React from 'react';
import { Alert } from 'reactstrap';
import AppButton from '../../components/AppButton';
import styled from 'styled-components';

export const Page = styled.div`
    min-height: 100vh;
    padding: var(--spacer-M);
    background:
        radial-gradient(circle at top left, var(--surface-brand-soft) 0%, transparent 42%),
        linear-gradient(180deg, var(--surface-brand-soft) 0%, white 42%, var(--bg-light) 100%);
`;

export const PageShell = styled.div`
    max-width: 920px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-M);
`;

const Card = styled.div`
    border: var(--border-thin) solid var(--surface-border-subtle);
    border-radius: 24px;
    box-shadow: 0 20px 45px rgba(0, 0, 0, 0.08);
    overflow: hidden;
`;

export const HeroCard = styled(Card)`
    background: linear-gradient(135deg, var(--brand-secondary) 0%, var(--brand-primary) 100%);
    color: var(--brand-accent);
`;

export const HeroInner = styled.div`
    padding: clamp(1.6rem, 4vw, 2.6rem);
    display: grid;
    grid-template-columns: minmax(0, 1.7fr) minmax(240px, 1fr);
    gap: var(--spacer-M);

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

export const HeroCopy = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

export const HeroBadge = styled.div`
    width: fit-content;
    padding: 0.45rem 0.9rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.16);
    border: var(--border-thin) solid rgba(255, 255, 255, 0.24);
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

export const CompanyHeader = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
`;

export const CompanyLogo = styled.img`
    width: 2.2rem;
    height: 2.2rem;
    border-radius: 8px;
    object-fit: contain;
    background: rgba(255, 255, 255, 0.18);
    flex-shrink: 0;
`;

export const CompanyName = styled.div`
    font-size: 0.95rem;
    font-weight: 600;
    opacity: 0.95;
`;

export const Title = styled.h1`
    margin: 0;
    font-size: clamp(2rem, 5vw, 3rem);
    line-height: 1.05;
    font-weight: 700;
`;

export const Subtitle = styled.div`
    margin: 0;
    max-width: 56ch;
    font-size: 1rem;
    line-height: 1.6;
    opacity: 0.94;

    p { margin: 0 0 0.4em; }
    p:last-child { margin-bottom: 0; }
    ul, ol { margin: 0 0 0.4em; padding-left: 1.4em; }
    strong { font-weight: 700; }
`;

export const HeroMetaGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--spacer-XS);

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

export const HeroMetaCard = styled.div`
    padding: 1rem;
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.12);
    border: var(--border-thin) solid rgba(255, 255, 255, 0.18);
`;

export const HeroMetaLabel = styled.div`
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.8;
    margin-bottom: var(--spacer-XXS);
`;

export const HeroMetaValue = styled.div`
    font-size: 1.05rem;
    font-weight: 700;
`;

export const HeroSidePanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
    justify-content: space-between;
`;

export const HeroSideCard = styled.div`
    padding: 1.2rem;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.12);
    border: var(--border-thin) solid rgba(255, 255, 255, 0.18);
`;

export const SideTitle = styled.div`
    font-size: 0.88rem;
    font-weight: 700;
    margin-bottom: var(--spacer-XXS);
`;

export const SideText = styled.p`
    margin: 0;
    font-size: 0.92rem;
    line-height: 1.55;
    opacity: 0.94;
`;

export const ContentCard = styled(Card)`
    background: white;
`;

export const ContentInner = styled.div`
    padding: clamp(1.2rem, 3.5vw, 2rem);
`;

export const BannerAlert = styled(Alert)`
    margin-bottom: var(--spacer-S);
    border-radius: 16px;
`;

export const SectionTitle = styled.h2`
    margin: 0 0 var(--spacer-XXS);
    color: var(--text-strong);
    font-size: 1.45rem;
    font-weight: 700;
`;

export const SectionText = styled.p`
    margin: 0;
    color: var(--text-muted);
    line-height: 1.6;
`;

export const QuestionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
    margin-top: var(--spacer-M);
`;

export const QuestionCard = styled.div`
    padding: 1.25rem;
    border-radius: 22px;
    background: linear-gradient(180deg, white 0%, var(--surface-brand-soft) 100%);
    border: var(--border-thin) solid var(--surface-border-subtle);
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.05);
`;

export const QuestionHeader = styled.div`
    display: flex;
    align-items: flex-start;
    gap: var(--spacer-S);
    margin-bottom: var(--spacer-S);
`;

export const QuestionNumber = styled.div`
    flex-shrink: 0;
    width: 2.3rem;
    height: 2.3rem;
    border-radius: 999px;
    background: var(--surface-brand-soft);
    color: var(--brand-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
`;

export const QuestionTitle = styled.div`
    color: var(--text-strong);
    font-size: 1.08rem;
    font-weight: 700;
    line-height: 1.45;
`;

export const MandatoryBadge = styled.span`
    display: inline-flex;
    margin-left: var(--spacer-XS);
    color: var(--danger-color-strong);
    font-size: 1rem;
    font-weight: 700;
    line-height: 1;
    vertical-align: top;
`;

export const QuestionHint = styled.div`
    color: var(--text-muted);
    font-size: 0.88rem;
    margin-top: var(--spacer-XXS);
`;

export const ChoiceList = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
`;

export const ChoiceButton = styled.button<{ $selected: boolean }>`
    width: 100%;
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
    text-align: left;
    padding: 0.95rem 1rem;
    border-radius: 18px;
    border: var(--border-thin) solid ${({ $selected }) => $selected ? 'var(--brand-primary)' : 'var(--appLigthGrey)'};
    background: ${({ $selected }) => $selected ? 'var(--surface-brand-soft)' : 'white'};
    color: var(--text-body);
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 10px 22px rgba(0, 0, 0, 0.06);
        border-color: var(--brand-primary);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.68;
    }
`;

export interface ChoiceIndicatorProps {
    $selected: boolean;
    children?: React.ReactNode;
}

export const ChoiceIndicator = styled.span<ChoiceIndicatorProps>`
    width: 1.1rem;
    height: 1.1rem;
    border-radius: 999px;
    border: 2px solid ${({ $selected }) => $selected ? 'var(--brand-secondary)' : 'var(--appLigthGrey)'};
    background: ${({ $selected }) => $selected ? 'var(--brand-secondary)' : 'white'};
    box-shadow: inset 0 0 0 3px white;
    flex-shrink: 0;
`;

export const ScaleChoiceGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(48px, 1fr));
    gap: var(--spacer-XS);
`;

export interface ScaleChoiceButtonProps {
    $selected: boolean;
    children: React.ReactNode;
    type?: string;
    onClick?: () => void;
    disabled?: boolean;
}

export const ScaleChoiceButton = styled.button<ScaleChoiceButtonProps>`
    min-height: 48px;
    border-radius: 16px;
    border: var(--border-thin) solid ${({ $selected }) => $selected ? 'var(--brand-secondary)' : 'var(--appLigthGrey)'};
    background: ${({ $selected }) => $selected ? 'var(--brand-secondary)' : 'white'};
    color: ${({ $selected }) => $selected ? 'white' : 'var(--text-body)'};
    font-weight: 700;
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
        border-color: var(--brand-primary);
    }

    &:disabled {
        opacity: 0.68;
        cursor: not-allowed;
    }
`;

export const ScaleFallback = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
`;

export const ScaleLabel = styled.span`
    min-width: 2rem;
    color: var(--text-muted);
    font-size: 0.88rem;
    font-weight: 600;
`;

export const ScaleValue = styled.div`
    min-width: 2.5rem;
    text-align: center;
    color: var(--brand-secondary);
    font-size: 1.1rem;
    font-weight: 700;
`;

export const StyledSlider = styled.input`
    flex-grow: 1;
    height: 6px;
    -webkit-appearance: none;
    appearance: none;
    background: linear-gradient(
        to right,
        var(--brand-secondary) 0%,
        var(--brand-secondary) var(--fill-pct, 50%),
        var(--appLigthGrey) var(--fill-pct, 50%),
        var(--appLigthGrey) 100%
    );
    border-radius: 999px;
    outline: none;
    cursor: pointer;

    &::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: var(--brand-secondary);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
        cursor: pointer;
        transition: transform 0.15s ease, box-shadow 0.15s ease;
    }

    &::-moz-range-thumb {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        border: none;
        background: var(--brand-secondary);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
        cursor: pointer;
        transition: transform 0.15s ease, box-shadow 0.15s ease;
    }

    &:hover::-webkit-slider-thumb,
    &:focus::-webkit-slider-thumb {
        transform: scale(1.15);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.22);
    }

    &:hover::-moz-range-thumb,
    &:focus::-moz-range-thumb {
        transform: scale(1.15);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.22);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

export const AnonymousSection = styled.div`
    margin-top: var(--spacer-M);
    padding: 1.15rem;
    border-radius: 20px;
    border: var(--border-thin) solid var(--surface-border-subtle);
    background: var(--surface-brand-soft);
`;

export const AnonymousTitle = styled.div`
    color: var(--text-strong);
    font-weight: 700;
    margin-bottom: var(--spacer-XXS);
`;

export const AnonymousDescription = styled.p`
    margin: var(--spacer-XS) 0 0;
    color: var(--text-muted);
    line-height: 1.6;
`;

export const FormFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacer-S);
    margin-top: var(--spacer-M);
    padding-top: var(--spacer-S);
    border-top: var(--border-thin) solid var(--surface-border-subtle);

    @media (max-width: 640px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

export const FooterText = styled.div`
    color: var(--text-muted);
    line-height: 1.6;
`;

export const SubmitButton = styled(AppButton)`
    min-width: 220px;
    min-height: 52px;
    border-radius: 999px;
    border: none;
    background: linear-gradient(135deg, var(--brand-secondary) 0%, var(--brand-primary) 100%);
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.18);

    &:hover:not(:disabled),
    &:focus:not(:disabled) {
        background: linear-gradient(135deg, var(--brand-secondary) 0%, var(--brand-primary) 100%);
        filter: brightness(0.95);
    }

    &:disabled {
        opacity: 0.7;
    }
`;

export const CenterState = styled(Card)`
    text-align: center;
    background: white;
`;

export const CenterStateInner = styled.div`
    padding: clamp(2rem, 5vw, 4rem) clamp(1.2rem, 4vw, 3rem);
`;

export const SuccessIcon = styled.div`
    width: 4rem;
    height: 4rem;
    margin: 0 auto var(--spacer-S);
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--success-bg-strong);
    color: var(--success-color-strong);
    font-size: 2rem;
    font-weight: 700;
`;

export const LoadingText = styled.p`
    margin: var(--spacer-S) 0 0;
    color: var(--text-muted);
`;
